import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { connectMonarch, disconnectMonarch } from '../../../redux/actions/import.js';
import TextInput from '../../elements/TextInput.jsx';
import Button from '../../components/Button.jsx';

/**
 * This budget's own Monarch sign-in (Monarch_Connections on the server), and the fetch it allows.
 *
 * The password and code are posted once, to sign in, and only the token Monarch answers with is kept.
 * So when Monarch stops honouring that token the server marks it for a reconnect, and this asks for
 * the password again rather than anything retrying on its own.
 */
export default function MonarchConnection({ onFetch, busy, fetching }) {
	const dispatch = useDispatch();
	const connection = useSelector((state) => state.importing.monarchConnection);
	const connecting = useSelector((state) => state.importing.connectingMonarch);
	const [formOpen, setFormOpen] = useState(false);

	if(connection === undefined) {
		return null;
	}

	const onDisconnect = () => {
		if(!confirm('Disconnect Monarch from this budget? What it imported stays; nightly fetches stop.')) {
			return;
		}
		dispatch(disconnectMonarch());
	};

	if(!connection) {
		return (
			<div className='monarch-connection'>
				{!formOpen &&
				<Button onClick={() => setFormOpen(true)} disabled={busy}>Connect Monarch</Button>
				}
				{formOpen &&
				<MonarchSignInForm
					connecting={connecting}
					onCancel={() => setFormOpen(false)}
					onSubmit={(fields) => dispatch(connectMonarch(fields))}
				/>
				}
			</div>
		);
	}

	const needsReconnect = connection.status === 'needs_reconnect';
	return (
		<div className='monarch-connection'>
			<p className='monarch-status'>
				Connected as {connection.email || 'an account'}
				{connection.connected_by && <> by {connection.connected_by}</>}.{' '}
				{connection.last_fetched_at
					? `Last fetched ${connection.last_fetched_at.slice(0, 10)}.`
					: 'Nothing fetched yet.'}
			</p>
			{needsReconnect &&
			<>
				<p className='red-text'>{connection.last_error || 'Monarch needs signing in again.'}</p>
				<MonarchSignInForm
					initialEmail={connection.email || ''}
					connecting={connecting}
					submitLabel='Reconnect'
					onSubmit={(fields) => dispatch(connectMonarch(fields))}
				/>
			</>
			}
			<div className='import-source-actions'>
				{!needsReconnect &&
				<Button
					onClick={onFetch}
					disabled={busy}
					title='Fetches every transaction Monarch has and stages it here to review, like an export.'
				>
					{fetching ? 'Fetching from Monarch…' : 'Fetch everything from Monarch'}
				</Button>
				}
				<Button flat onClick={onDisconnect} disabled={busy || connecting}>Disconnect</Button>
			</div>
		</div>
	);
}

/**
 * Keeps password managers out of these fields. They are Monarch's, not this app's, so the browser
 * should neither fill in the budget login here nor offer to save Monarch's as the budget's.
 * autocomplete hints alone do not do it -- browsers treat any type=password field as a login -- so
 * the password is a text field masked by CSS where the browser can mask one.
 */
const NOT_A_LOGIN = {
	autoComplete: 'off',
	spellCheck: false,
	autoCapitalize: 'off',
	'data-1p-ignore': true,
	'data-lpignore': 'true',
	'data-bwignore': true,
	'data-form-type': 'other',
};

/** A text field the browser masks, where it can; otherwise a password field told not to autofill. */
export function maskedSecretProps(css = typeof CSS === 'undefined' ? undefined : CSS) {
	if(css && typeof css.supports === 'function' && css.supports('-webkit-text-security', 'disc')) {
		return { type: 'text', className: 'masked-secret' };
	}
	return { type: 'password', autoComplete: 'new-password' };
}

/** Whether a typed authentication code can be sent: blank, or six digits. Apps show "123 456". */
export function oneTimeCodeReady(code) {
	return /^(\d{6})?$/.test(code.replace(/\s+/g, ''));
}

/**
 * What to say about a typed authentication code, or null. Fewer than six digits is a code still being
 * typed, not a mistake. A long run of letters and digits is the setup key the app was given when
 * two-factor was turned on, which is the easy thing to paste by mistake.
 */
export function oneTimeCodeProblem(code) {
	const digits = code.replace(/\s+/g, '');
	if(oneTimeCodeReady(code) || /^\d{1,5}$/.test(digits)) {
		return null;
	}
	if(/^[A-Za-z2-7=]{16,}$/.test(digits)) {
		return 'That is the setup key, not a code. Enter the 6 digits the app shows.';
	}
	return 'Enter the 6 digits your authenticator app shows.';
}

/**
 * Resolves the submit to whether it connected, and clears the secrets on success so they do not sit
 * in the page's state afterwards.
 */
function MonarchSignInForm({ initialEmail = '', connecting, submitLabel = 'Connect', onSubmit, onCancel }) {
	const [email, setEmail] = useState(initialEmail);
	const [password, setPassword] = useState('');
	const [code, setCode] = useState('');
	const codeProblem = oneTimeCodeProblem(code);

	const submit = (event) => {
		event.preventDefault();
		Promise.resolve(onSubmit({ email, password, code })).then((connected) => {
			if(connected) {
				setPassword('');
				setCode('');
			}
		});
	};

	return (
		<form className='monarch-sign-in' onSubmit={submit} autoComplete='off'>
			<p className='grey-text'>
				Your password and code are used once, to sign in. Only the session Monarch hands back is
				kept, encrypted, and when Monarch ends it you will be asked to sign in again here.
			</p>
			<div className='input-field'>
				<TextInput
					label='Monarch email'
					{...NOT_A_LOGIN}
					inputMode='email'
					value={email}
					disabled={connecting}
					onChange={(event) => setEmail(event.target.value)}
				/>
			</div>
			<div className='input-field'>
				<TextInput
					label='Monarch password'
					{...NOT_A_LOGIN}
					{...maskedSecretProps()}
					value={password}
					disabled={connecting}
					onChange={(event) => setPassword(event.target.value)}
				/>
			</div>
			<div className='input-field'>
				<TextInput
					label='Authentication code'
					{...NOT_A_LOGIN}
					autoComplete='one-time-code'
					inputMode='numeric'
					placeholder='123 456'
					className='one-time-code'
					value={code}
					disabled={connecting}
					error={codeProblem}
					onChange={(event) => setCode(event.target.value)}
				/>
				{!codeProblem &&
				<span className='helper-text'>
					The 6-digit code your authenticator app shows for Monarch right now. Leave it blank if
					the account has no two-factor.
				</span>
				}
			</div>
			<div className='import-source-actions'>
				<Button submit disabled={connecting || !email || !password || !oneTimeCodeReady(code)}>
					{connecting ? 'Signing in…' : submitLabel}
				</Button>
				{onCancel && <Button flat onClick={onCancel} disabled={connecting}>Cancel</Button>}
			</div>
		</form>
	);
}
